import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Plus } from 'lucide-react'
import { listAccounts } from '@/api/accounts'
import { listCategories } from '@/api/categories'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { useReadOnlyKnown } from '@/hooks/useReadOnlyKnown'
import { useFilterMode, useTransactionFilters } from '@/hooks/useTransactionFilters'
import { useTransactionPages } from '@/hooks/useTransactionPages'
import { Button } from '@/components/ui/button'
import { AddTransactionModal } from '@/components/transactions/TransactionForms'
import type { AddKind } from '@/components/transactions/add/addKinds'
import { TransactionFiltersBar } from '@/components/transactions/TransactionFilters'
import { filterChips, scopeLabels, useFilterOptions } from '@/components/transactions/filterChips'
import { TransactionList } from '@/components/transactions/TransactionList'
import { TransactionsHero } from '@/components/transactions/TransactionsHero'
import { currentMonth } from '@/lib/transactionDateRangeFilter'
import { backLabel } from '@/lib/transactionsLink'
import { cn } from '@/lib/utils'

const ADD_PARAM_KINDS: Record<string, AddKind> = {
  EXPENSE: 'spent',
  INCOME: 'received',
  TRANSFER: 'transfer',
}

export default function TransactionsPage() {
  const isReadOnly = useIsReadOnly()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const [selecting, setSelecting] = useState(false)
  const readOnlyKnown = useReadOnlyKnown()
  const { filters, back, setFilters, update, clear } = useTransactionFilters()
  const { mode, setMode } = useFilterMode()
  const options = useFilterOptions()
  const pages = useTransactionPages(filters)
  const [searchParams, setSearchParams] = useSearchParams()
  // Analytics nudges link here with ?add=INCOME|EXPENSE|TRANSFER. Read-only is only known once its
  // queries load, so the modal is gated at render, and waits until it is known.
  const [addKind, setAddKind] = useState<AddKind | undefined>(
    () => ADD_PARAM_KINDS[searchParams.get('add') ?? '']
  )
  const [showAdd, setShowAdd] = useState(addKind !== undefined)
  // Only a form a link opened waits for read-only to be known: a click on Add comes after the page loads.
  const [openedByLink] = useState(showAdd)
  useEffect(() => {
    if (!searchParams.has('add')) return
    const next = new URLSearchParams(searchParams)
    next.delete('add')
    setSearchParams(next, { replace: true })
    // Once on mount: later param changes must not reopen the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data: accountsData } = useQuery({ queryKey: ['accounts'], queryFn: () => listAccounts() })
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => listCategories(),
  })

  const chips = filterChips(filters, options)
  const hasFilters =
    chips.length > 0 || !!filters.type || !!filters.q || filters.month !== currentMonth()
  const isEmpty = !pages.isLoading && !pages.isError && pages.transactions.length === 0
  const canSelect = !isReadOnly && !selecting && pages.transactions.length > 0

  return (
    <div className="flex flex-col gap-3 p-4 sm:p-6 md:gap-4 md:p-10">
      {back && (
        <Link
          to={back}
          className="flex min-h-8 items-center gap-1 self-start text-sm font-medium text-[var(--fl-primary)] hover:underline"
        >
          <ArrowLeft size={16} aria-hidden="true" /> {backLabel(back)}
        </Link>
      )}

      <header className="flex items-center justify-between gap-3">
        <h1 className="font-serif text-xl font-semibold text-[var(--fl-heading)] md:text-[32px]">
          Transactions
        </h1>
        {/* A phone adds from the tab bar's ＋; this one is for wider screens. */}
        {!isReadOnly && (
          <Button
            aria-label="Add transaction"
            onClick={() => setShowAdd(true)}
            className={cn(
              'hidden h-10 gap-1.5 rounded-full px-4 font-semibold md:inline-flex',
              isEmpty && !hasFilters && 'motion-safe:animate-attention-pulse'
            )}
          >
            <Plus size={16} aria-hidden="true" /> Add
          </Button>
        )}
      </header>

      <TransactionsHero data={pages.data} filters={filters} scopeLabels={scopeLabels(chips)} />

      <TransactionFiltersBar
        filters={filters}
        options={options}
        mode={mode}
        onModeChange={setMode}
        onChange={setFilters}
        onUpdate={update}
        onClearAll={clear}
        onSelect={!wide && canSelect ? () => setSelecting(true) : undefined}
      />

      <TransactionList
        pages={pages}
        hasFilters={hasFilters}
        onClearFilters={clear}
        selectable
        selecting={selecting}
        onSelectingChange={setSelecting}
        hideSelectButton={!wide}
        focusAccountId={filters.financialAccountId}
      />

      {showAdd && !isReadOnly && (readOnlyKnown || !openedByLink) && (
        <AddTransactionModal
          initialKind={addKind}
          accounts={accountsData?.accounts ?? []}
          categories={categoriesData?.categories ?? []}
          onClose={() => {
            setShowAdd(false)
            setAddKind(undefined)
          }}
        />
      )}
    </div>
  )
}
